import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, CircularProgress, Alert, Button, Divider,
  Table, TableBody, TableCell, TableContainer, TableRow, Paper, Card, CardContent, Grid, Chip, Tabs, Tab
} from '@mui/material';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import EventIcon from '@mui/icons-material/Event';
import InfoIcon from '@mui/icons-material/Info';
import AssignmentIcon from '@mui/icons-material/Assignment';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { api } from '../api/client';
import { useDecodedToken } from '../utils/useDecodedToken';
import LoanStatementContainer from '../components/statement/LoanStatementContainer';
import LoanPaymentSummary from '../components/payments/LoanPaymentSummary';
import PaymentHistoryTable from '../components/payments/PaymentHistoryTable';

const statusColor = (status: string | undefined) => {
  switch ((status || '').toLowerCase()) {
    case 'approved': return 'success';
    case 'active': return 'primary';
    case 'overpaid': return 'warning';
    case 'pending': return 'info';
    case 'closed': return 'default';
    case 'rejected': return 'error';
    default: return 'secondary';
  }
};

const SECTIONS = [
  { label: 'Loan Details', icon: <AssignmentIcon sx={{ mb: '-4px', mr: 1 }} /> },
  { label: 'Loan Statement', icon: <AssignmentIcon sx={{ mb: '-4px', mr: 1 }} /> },
  { label: 'Payments', icon: <MonetizationOnIcon sx={{ mb: '-4px', mr: 1 }} /> },
  { label: 'Transactions', icon: <EventIcon sx={{ mb: '-4px', mr: 1 }} /> },
  { label: 'Charges', icon: <InfoIcon sx={{ mb: '-4px', mr: 1 }} /> },
  { label: 'Repayment Schedule', icon: <AssignmentIcon sx={{ mb: '-4px', mr: 1 }} /> },
];

const LoanDetails: React.FC = () => {
  const { loanId } = useParams<{ loanId: string }>();
  const navigate = useNavigate();
  const user = useDecodedToken();
  const [loan, setLoan] = useState<any>(null);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [loanCharges, setLoanCharges] = useState<any[]>([]);
  const [repaymentSchedule, setRepaymentSchedule] = useState<any[]>([]);
  const [statementData, setStatementData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [section, setSection] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const refreshData = async () => {
    setRefreshing(true);
    try {
      // Refresh loan details
      const loanRes = await api.get(`/api/loans/${loanId}`);
      setLoan(loanRes.data);
      
      // Refresh current section data if needed
      if (section === 1) {
        // Refresh statement data
        const statementRes = await api.get(`/api/fineract-loans/${loanId}/transaction-history`);
        const loanTransactions = statementRes.data.content || [];
        
        const transformedData = {
          borrowerName: loan.clientName || 'N/A',
          coApplicantName: '',
          address: '',
          loanNumber: loan.accountNo || loan.loanNumber || 'N/A',
          disbursedAmount: loan.summary?.principalDisbursed || loan.disbursedAmount || 0,
          termOfLoan: loan.termFrequency || 0,
          termUnit: loan.termPeriodFrequencyType?.value || 'Months',
          rateOfInterest: loan.annualInterestRate || loan.interestRatePerPeriod || 0,
          emiStartDate: '',
          periodFrom: loan.timeline?.submittedOnDate ? 
            new Date(loan.timeline.submittedOnDate[0], loan.timeline.submittedOnDate[1] - 1, loan.timeline.submittedOnDate[2]).toLocaleDateString() : '',
          periodTo: loan.timeline?.expectedMaturityDate ? 
            new Date(loan.timeline.expectedMaturityDate[0], loan.timeline.expectedMaturityDate[1] - 1, loan.timeline.expectedMaturityDate[2]).toLocaleDateString() : '',
          transactions: (() => {
            let runningBalance = 0;
            const rows = loanTransactions.map((tx: any, index: number) => {
              const txnDate = tx.date ? new Date(tx.date[0], tx.date[1] - 1, tx.date[2]).toLocaleDateString() : 'N/A';
              const voucher = tx.id?.toString() || `TXN-${index + 1}`;
              const narration = tx.type?.value || 'Transaction';
              const debit = Number(tx.debit || 0);
              const credit = Number(tx.credit || 0);
              runningBalance = runningBalance + debit - credit;
              return { txnDate, voucher, narration, debit, credit, balance: Number(runningBalance.toFixed(2)) };
            });
            const totals = rows.reduce((acc, r) => ({ debit: acc.debit + r.debit, credit: acc.credit + r.credit }), { debit: 0, credit: 0 });
            rows.push({ txnDate: 'Grand Total', voucher: '', narration: '', debit: Number(totals.debit.toFixed(2)), credit: Number(totals.credit.toFixed(2)), balance: Number(runningBalance.toFixed(2)) });
            return rows;
          })(),
          roiHistory: [],
          pageInfo: {
            currentPage: 1,
            totalPages: 1,
            takenBy: loan.loanOfficerName || 'System',
            dateTime: new Date().toLocaleString(),
          }
        };

        setStatementData(transformedData);
      } else if (section === 3) {
        const txRes = await api.get(`/api/loans/${loanId}/associations?associations=transactions`);
        const transactions = txRes.data.transactions || [];
        setTransactions(Array.isArray(transactions) ? transactions : []);
      } else if (section === 4) {
        const chargesRes = await api.get(`/api/loans/${loanId}/charges`);
        const charges = chargesRes.data || [];
        setLoanCharges(Array.isArray(charges) ? charges : []);
      } else if (section === 5) {
        const scheduleRes = await api.get(`/api/loans/${loanId}/associations?associations=repaymentSchedule`);
        const schedule = scheduleRes.data.repaymentSchedule?.periods || [];
        setRepaymentSchedule(Array.isArray(schedule) ? schedule : []);
      }
    } catch (err: any) {
      console.error('Failed to refresh data:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const formatDueDate = (dueDateArray: number[]) => {
    if (!dueDateArray || dueDateArray.length !== 3) return 'N/A';
    const [year, month, day] = dueDateArray;
    return new Date(year, month - 1, day).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  useEffect(() => {
    const fetchLoanDetails = async () => {
      setLoading(true);
      setError('');
      try {
        const loanRes = await api.get(`/api/loans/${loanId}`);
        setLoan(loanRes.data);
      } catch (err: any) {
        setError('Failed to fetch loan details.');
      } finally {
        setLoading(false);
      }
    };
    if (loanId) fetchLoanDetails();
  }, [loanId]);

  // Fetch loan transactions when transactions tab is selected
  useEffect(() => {
    const fetchLoanTransactions = async () => {
      if (section === 3 && loanId) {
        try {
          const txRes = await api.get(`/api/loans/${loanId}/associations?associations=transactions`);
          const transactions = txRes.data.transactions || [];
          // Ensure it's always an array
          setTransactions(Array.isArray(transactions) ? transactions : []);
        } catch (err: any) {
          console.error('Failed to fetch loan transactions:', err);
          setTransactions([]);
        }
      }
    };
    fetchLoanTransactions();
  }, [section, loanId]);

  // Fetch loan charges when charges tab is selected
  useEffect(() => {
    const fetchLoanCharges = async () => {
      if (section === 4 && loanId) {
        try {
          const chargesRes = await api.get(`/api/loans/${loanId}/charges`);
          const charges = chargesRes.data || [];
          // Ensure it's always an array
          setLoanCharges(Array.isArray(charges) ? charges : []);
        } catch (err: any) {
          console.error('Failed to fetch loan charges:', err);
          setLoanCharges([]);
        }
      }
    };
    fetchLoanCharges();
  }, [section, loanId]);

  // Fetch repayment schedule when repayment schedule tab is selected
  useEffect(() => {
    const fetchRepaymentSchedule = async () => {
      if (section === 5 && loanId) {
        try {
          const scheduleRes = await api.get(`/api/loans/${loanId}/associations?associations=repaymentSchedule`);
          const schedule = scheduleRes.data.repaymentSchedule?.periods || [];
          // Ensure it's always an array
          setRepaymentSchedule(Array.isArray(schedule) ? schedule : []);
        } catch (err: any) {
          console.error('Failed to fetch repayment schedule:', err);
          setRepaymentSchedule([]);
        }
      }
    };
    fetchRepaymentSchedule();
  }, [section, loanId]);

  // Fetch statement data when statement tab is selected
  useEffect(() => {
    const fetchStatementData = async () => {
      if (section === 1 && loanId && loan) {
        try {
          const statementRes = await api.get(`/api/loans/${loanId}/associations?associations=transactions`);
          const loanTransactions = statementRes.data.transactions || [];
          
          // Transform the data to match the statement component format
          const transformedData = {
            borrowerName: loan.clientName || 'N/A',
            coApplicantName: '',
            address: '',
            loanNumber: loan.accountNo || loan.loanNumber || 'N/A',
            disbursedAmount: loan.summary?.principalDisbursed || loan.disbursedAmount || 0,
            termOfLoan: loan.termFrequency || 0,
            termUnit: loan.termPeriodFrequencyType?.value || 'Months',
            rateOfInterest: loan.annualInterestRate || loan.interestRatePerPeriod || 0,
            emiStartDate: '',
            periodFrom: loan.timeline?.submittedOnDate ? 
              new Date(loan.timeline.submittedOnDate[0], loan.timeline.submittedOnDate[1] - 1, loan.timeline.submittedOnDate[2]).toLocaleDateString() : '',
            periodTo: loan.timeline?.expectedMaturityDate ? 
              new Date(loan.timeline.expectedMaturityDate[0], loan.timeline.expectedMaturityDate[1] - 1, loan.timeline.expectedMaturityDate[2]).toLocaleDateString() : '',
            transactions: (() => {
              // Map and categorize to debit/credit first
              const baseRows = loanTransactions.map((tx: any, index: number) => {
                const transactionType = tx.type?.value || '';
                const description = tx.description || '';
                const amount = tx.amount || 0;
                let debit = 0;
                let credit = 0;
                if (transactionType.toLowerCase().includes('repayment') || 
                    transactionType.toLowerCase().includes('payment') ||
                    description.toLowerCase().includes('repayment') ||
                    description.toLowerCase().includes('payment') ||
                    description.toLowerCase().includes('repay')) {
                  credit = amount;
                  debit = 0;
                } else if (transactionType.toLowerCase().includes('disbursement') ||
                           transactionType.toLowerCase().includes('accrual') ||
                           transactionType.toLowerCase().includes('interest') ||
                           transactionType.toLowerCase().includes('charge') ||
                           description.toLowerCase().includes('disbursement') ||
                           description.toLowerCase().includes('accrual') ||
                           description.toLowerCase().includes('interest') ||
                           description.toLowerCase().includes('charge')) {
                  debit = amount;
                  credit = 0;
                } else {
                  debit = amount;
                  credit = 0;
                }
                return {
                  txnDate: tx.date ? new Date(tx.date[0], tx.date[1] - 1, tx.date[2]).toLocaleDateString() : 'N/A',
                  voucher: tx.id?.toString() || `TXN-${index + 1}`,
                  narration: tx.type?.value || 'Transaction',
                  debit: Number(debit || 0),
                  credit: Number(credit || 0),
                  balance: 0,
                };
              });
              // Compute running balance and totals
              let runningBalance = 0;
              const rowsWithBalance = baseRows.map(r => {
                runningBalance = runningBalance + r.debit - r.credit;
                return { ...r, balance: Number(runningBalance.toFixed(2)) };
              });
              const totals = rowsWithBalance.reduce((acc, r) => ({
                debit: acc.debit + r.debit,
                credit: acc.credit + r.credit,
              }), { debit: 0, credit: 0 });
              // Append Grand Total row
              rowsWithBalance.push({
                txnDate: 'Grand Total',
                voucher: '',
                narration: '',
                debit: Number(totals.debit.toFixed(2)),
                credit: Number(totals.credit.toFixed(2)),
                balance: Number(runningBalance.toFixed(2)),
              });
              return rowsWithBalance;
            })(),
            roiHistory: [],
            pageInfo: {
              currentPage: 1,
              totalPages: 1,
              takenBy: loan.loanOfficerName || 'System',
              dateTime: new Date().toLocaleString(),
            }
          };
          
          setStatementData(transformedData);
        } catch (err: any) {
          console.error('Failed to fetch statement data:', err);
          setStatementData(null);
        }
      }
    };
    fetchStatementData();
  }, [section, loanId, loan]);

  return (
    <Box sx={{ p: 3, maxWidth: 700, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ mr: 1 }}
          >
            Back
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: 1 }}>
            <AssignmentIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} /> Loan Details
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={refreshData}
          disabled={refreshing}
          sx={{ 
            minWidth: 'auto',
            px: 2,
            py: 1,
            borderRadius: 2,
            borderColor: 'primary.main',
            color: 'primary.main',
            '&:hover': {
              borderColor: 'primary.dark',
              backgroundColor: 'primary.50'
            }
          }}
        >
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </Button>
      </Box>
      <Tabs
        value={section}
        onChange={(_, v) => setSection(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        {SECTIONS.map((s, i) => (
          <Tab key={s.label} label={<Box sx={{ display: 'flex', alignItems: 'center' }}>{s.icon}{s.label}</Box>} />
        ))}
      </Tabs>
      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : loan ? (
        <>
          {section === 0 && (
            <Card elevation={4} sx={{ mb: 4, borderRadius: 3, background: '#f8fafc' }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                      {loan.loanProductName || loan.loanType?.value} <MonetizationOnIcon sx={{ color: 'success.main', ml: 1, mb: '-4px' }} />
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Chip
                      label={loan.status?.value}
                      color={statusColor(loan.status?.value)}
                      size="medium"
                      sx={{ fontWeight: 600, fontSize: 18, px: 2, py: 1, mb: 2, textTransform: 'capitalize' }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TableContainer component={Paper}>
                      <Table size="small">
                        <TableBody>
                          <TableRow><TableCell><b>Loan Number</b></TableCell><TableCell>{loan.loanNumber || loan.accountNo}</TableCell></TableRow>
                          <TableRow><TableCell><b>Amount</b></TableCell><TableCell>{loan.principal ?? loan.amount}</TableCell></TableRow>
                          <TableRow><TableCell><b>Currency</b></TableCell><TableCell>{loan.currency?.code}</TableCell></TableRow>
                          <TableRow><TableCell><b>Interest Rate</b></TableCell><TableCell>{loan.annualInterestRate ?? loan.interestRate}%</TableCell></TableRow>
                          <TableRow><TableCell><b>Term</b></TableCell><TableCell>{loan.termFrequency} {loan.termPeriodFrequencyType?.value}</TableCell></TableRow>
                          <TableRow><TableCell><b>Repayment Freq</b></TableCell><TableCell>{loan.repaymentEvery} {loan.repaymentFrequencyType?.value}</TableCell></TableRow>
                          <TableRow><TableCell><b>Outstanding</b></TableCell><TableCell>{loan.summary?.principalOutstanding ?? loan.outstandingBalance}</TableCell></TableRow>
                          <TableRow><TableCell><b>Disbursed</b></TableCell><TableCell>{loan.summary?.principalDisbursed ?? loan.disbursedAmount}</TableCell></TableRow>
                          <TableRow><TableCell><b>Purpose</b></TableCell><TableCell>{loan.purpose}</TableCell></TableRow>
                          <TableRow><TableCell><b>Collateral</b></TableCell><TableCell>{loan.collateralType} {loan.collateralValue ? `(${loan.collateralValue})` : ''}</TableCell></TableRow>
                          <TableRow><TableCell><b>Loan Type</b></TableCell><TableCell>{loan.loanType?.value}</TableCell></TableRow>
                          <TableRow><TableCell><b>Amortization</b></TableCell><TableCell>{loan.amortizationType?.value}</TableCell></TableRow>
                          <TableRow><TableCell><b>Interest Type</b></TableCell><TableCell>{loan.interestType?.value}</TableCell></TableRow>
                          <TableRow><TableCell><b>Interest Calc</b></TableCell><TableCell>{loan.interestCalculationPeriodType?.value}</TableCell></TableRow>
                          <TableRow><TableCell><b>Client</b></TableCell><TableCell>{loan.clientName}</TableCell></TableRow>
                          <TableRow><TableCell><b>Officer</b></TableCell><TableCell>{loan.loanOfficerName}</TableCell></TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}
          {section === 1 && (
            <Box>
              {statementData ? (
                <LoanStatementContainer statementData={statementData} />
              ) : (
                <Typography>Loading statement data...</Typography>
              )}
            </Box>
          )}
          {section === 2 && (
            <Box>
              <LoanPaymentSummary
                loanId={Number(loanId)}
                loanNumber={loan?.accountNo || loan?.loanNumber || 'N/A'}
                onPaymentInitiated={() => {
                  // Optionally refresh loan data or show success message

                }}
              />
              <Box sx={{ mt: 3 }}>
                <PaymentHistoryTable loanId={Number(loanId)} />
              </Box>
            </Box>
          )}
          {section === 3 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}><EventIcon sx={{ mb: '-4px', mr: 1, color: 'primary.main' }} /> Transactions</Typography>
              {(!transactions || !Array.isArray(transactions) || transactions.length === 0) ? (
                <Typography>No transactions found.</Typography>
              ) : (
                <TableContainer component={Paper} sx={{ mb: 4 }}>
                  <Table size="small">
                    <TableBody>
                      <TableRow sx={{ background: '#f0f4f8' }}>
                        <TableCell><b>Date</b></TableCell>
                        <TableCell><b>Type</b></TableCell>
                        <TableCell><b>Amount</b></TableCell>
                        <TableCell><b>Status</b></TableCell>
                        <TableCell><b>Description</b></TableCell>
                      </TableRow>
                      {transactions.map((tx) => (
                        <TableRow key={tx.id}>
                          <TableCell>{tx.date ? new Date(tx.date[0], tx.date[1] - 1, tx.date[2]).toLocaleDateString() : 'N/A'}</TableCell>
                          <TableCell>{tx.type?.value || 'N/A'}</TableCell>
                          <TableCell>{tx.amount?.toFixed(2) || '0.00'}</TableCell>
                          <TableCell>
                            <Chip 
                              label={tx.manuallyReversed ? 'Reversed' : 'Active'} 
                              color={tx.manuallyReversed ? 'error' : 'success'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{tx.type?.value || 'N/A'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
          {section === 4 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}><InfoIcon sx={{ mb: '-4px', mr: 1, color: 'primary.main' }} /> Loan Charges</Typography>
              {(!loanCharges || !Array.isArray(loanCharges) || loanCharges.length === 0) ? (
                <Typography color="text.secondary">No loan charges found.</Typography>
              ) : (
                <TableContainer component={Paper} sx={{ mb: 4 }}>
                  <Table size="small">
                    <TableBody>
                      <TableRow sx={{ background: '#f0f4f8' }}>
                        <TableCell><b>Charge Name</b></TableCell>
                        <TableCell><b>Due Date</b></TableCell>
                        <TableCell><b>Amount</b></TableCell>
                        <TableCell><b>Currency</b></TableCell>
                        <TableCell><b>Status</b></TableCell>
                        <TableCell><b>Calculation Type</b></TableCell>
                      </TableRow>
                      {loanCharges.map((charge) => (
                        <TableRow key={charge.id}>
                          <TableCell>{charge.name}</TableCell>
                          <TableCell>{formatDueDate(charge.dueDate)}</TableCell>
                          <TableCell>{charge.amount?.toFixed(2)}</TableCell>
                          <TableCell>{charge.currency?.code}</TableCell>
                          <TableCell>
                            <Chip 
                              label={charge.paid ? 'Paid' : 'Outstanding'} 
                              color={charge.paid ? 'success' : 'warning'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{charge.chargeCalculationType?.value}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
          {section === 5 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}><AssignmentIcon sx={{ mb: '-4px', mr: 1, color: 'primary.main' }} /> Repayment Schedule</Typography>
              {(!repaymentSchedule || !Array.isArray(repaymentSchedule) || repaymentSchedule.length === 0) ? (
                <Typography color="text.secondary">No repayment schedule found.</Typography>
              ) : (
                <TableContainer component={Paper} sx={{ mb: 4 }}>
                  <Table size="small">
                    <TableBody>
                                            <TableRow sx={{ background: '#f0f4f8' }}>
                        <TableCell><b>Period</b></TableCell>
                        <TableCell><b>Due Date</b></TableCell>
                        <TableCell><b>Principal Due</b></TableCell>
                        <TableCell><b>Principal Paid</b></TableCell>
                        <TableCell><b>Interest Due</b></TableCell>
                        <TableCell><b>Penalty Due</b></TableCell>
                        <TableCell><b>Total Due</b></TableCell>
                        <TableCell><b>Total Paid</b></TableCell>
                        <TableCell><b>Status</b></TableCell>
                      </TableRow>
                      {repaymentSchedule.map((period, index) => (
                        <TableRow key={period.period || index}>
                          <TableCell>{period.period || index + 1}</TableCell>
                          <TableCell>
                            {period.dueDate ? 
                              new Date(period.dueDate[0], period.dueDate[1] - 1, period.dueDate[2]).toLocaleDateString() : 
                              'N/A'
                            }
                          </TableCell>
                          <TableCell>{period.principalDue?.toFixed(2) || '0.00'}</TableCell>
                          <TableCell>{period.principalPaid?.toFixed(2) || '0.00'}</TableCell>
                          <TableCell>{period.interestDue?.toFixed(2) || '0.00'}</TableCell>
                          <TableCell>{period.penaltyChargesDue?.toFixed(2) || '0.00'}</TableCell>
                          <TableCell>{period.totalDueForPeriod?.toFixed(2) || '0.00'}</TableCell>
                          <TableCell>{period.totalPaidForPeriod?.toFixed(2) || '0.00'}</TableCell>
                          <TableCell>
                            <Chip 
                              label={period.complete ? 'Complete' : 'Pending'}
                              color={period.complete ? 'success' : 'warning'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

        </>
      ) : (
        <Typography>No loan details found.</Typography>
      )}
    </Box>
  );
};

export default LoanDetails; 
